import { ChangeDetectorRef, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { BaseComponent, ResponseService, WindowRef } from '@portals/core';
import { AuthState, ChannelsState, NotificationsState, UsersState } from '@portals/store';
import { AWSUploadService, Channel, CollectibleService, LINK_DOMAINS, LINK_RESPONSE, LINK_TYPES, LinkService, Notification, ThemeType } from '@portals/api';
import { map, mergeMap, tap, finalize, Subject, bufferCount, catchError, of } from 'rxjs';
import { NgxQrcodeElementTypes, NgxQrcodeErrorCorrectionLevels } from '@techiediaries/ngx-qrcode';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@portals/environments';
import { RESPONSE_TYPES, SCREENS } from '@portals/utils';
import mqtt, { MqttClient } from 'mqtt';
import { CookieService } from 'ngx-cookie';

@Component({
	selector: 'portals-home',
	templateUrl: './home.component.html',
	styleUrls: ['./home.component.scss'],
})
export class HomeComponent extends BaseComponent implements OnInit {
	protected readonly LINK_TYPES = LINK_TYPES;
	protected readonly SCREENS = SCREENS;

	url!: string;
	channel!: Channel;
	feed!: Notification[];
	notification!: Notification;
	mqttClient?: MqttClient;
	private theme?: ThemeType;
	channelUUID = environment.defaultChannel as string; // 2b878fba-a0fc-4845-9b9b-e0c6de090006 for Joe Biden, 77c8d433-99e1-473d-b96f-d4359af460a5 for staging, f77317f7-895f-4570-aa9e-93b1ddb00f47 for
	// adams
	notificationID = environment.defaultNotification as number; // 1177 for Joe Biden, 2199 for staging, 1210 for adams
	isMobile = false;
	showScrollToNextPageBtn = false;
	elementType = NgxQrcodeElementTypes.URL;
	correctionLevel = NgxQrcodeErrorCorrectionLevels.HIGH;
	grayPortionHeight = 0;
	qrCodeColor = {
		dark: '#491176',
		light: '#fbd354',
	};
	NgxQrcodeElementTypes = {
		URL: 'url',
		IMG: 'img',
		CANVAS: 'canvas',
		SVG: 'svg',
	};
	ready = false;
	wordCount: { counter: number; words: any[]; autorefresh: boolean } = { counter: 0, words: [], autorefresh: true };
	isSTV = false;
	currentLanguage = 'en';
	customLocalizationSet = false;

	constructor(
		private channelState: ChannelsState,
		private notificationState: NotificationsState,
		public translate: TranslateService,
		public deviceService: DeviceDetectorService,
		public windowRef: WindowRef,
		private awsUpload: AWSUploadService,
		private collectibleService: CollectibleService,
		public cdr: ChangeDetectorRef,
		private user: UsersState,
		public linkService: LinkService,
		private router: Router,
		private cookies: CookieService,
		private route: ActivatedRoute,
		public responseService: ResponseService,
		private auth: AuthState
	) {
		super();

		// Localization stuff
		const systemDefaultLanguage = navigator.language.split('-')[0];
		const supportedLanguages = ['en', 'es', 'ar']; // Add more languages as needed
		const defaultLanguage = supportedLanguages.includes(systemDefaultLanguage) ? systemDefaultLanguage : 'en';
		translate.setDefaultLang(defaultLanguage);
		this.currentLanguage = systemDefaultLanguage;

		this.route.params.subscribe((params) => {
			this.route.queryParams.subscribe((queryParams) => {
				const url = window.location.href;
				const preparedUrl = url.startsWith('http') ? url : `https://${url}`;
				const hostname = ('https://' + new URL(preparedUrl).hostname + '/') as LINK_DOMAINS;
				params['link']
					? !queryParams['redirected']
						? this.linkService
								.getLink(hostname.replace(/www\./g, ""), params['link'])
								.pipe(
									catchError((err) => of(err)),
									map((data: LINK_RESPONSE) => {
										switch (data.type) {
											case LINK_TYPES.CHANNEL:
												this.channel = data.entity as Channel;
												this.channelUUID = <string>this.channel.uuid;
												return;
											case LINK_TYPES.PROMPT:
												this.notification = data.entity as Notification;
												this.channelUUID = <string>this.notification.channel?.uuid;
												this.notificationID = this.notification.id;
												this.isSTV = true;
												if (this.notification?.localization) {
													this.currentLanguage = this.notification.localization;
													this.customLocalizationSet = true;
													translate.setDefaultLang(this.notification.localization);
												}
												return;
											default:
												return;
										}
									}),
									mergeMap(() => {
										return this.initializePage();
									})
								)
								.subscribe()
						: this.linkService.sendToAppStore(queryParams['type'], hostname.replace(/www\./g, "") + params['link'])
					: this.notificationState
							.loadItem(this.notificationID)
							.pipe(
								mergeMap((data: Notification) => {
                                    console.log("here", data)
                                    this.notification = data;
                                    this.isSTV = true;
                                    if (this.notification?.localization) {
                                        this.currentLanguage = this.notification.localization;
                                        this.customLocalizationSet = true;
                                        translate.setDefaultLang(this.notification.localization);
                                    }
                                    console.log(this.notification?.qr_code);

                                    return this.initializePage();
								})
							)
							.subscribe();
			});
		});
		this.isMobile = deviceService.isMobile();
	}

	observeTopic(topic: string) {
		if (this.mqttClient) {
			this.mqttClient.subscribe(topic, (err) => {
				if (err) {
					console.error('Error subscribing to MQTT topic:', err);
				} else {
					console.log('Subscribed to MQTT topic:', topic);
				}
			});
		}
	}

	fetchData() {
		this.collectibleService
			.momentQuery(this.notificationID)
			.pipe(
				tap((data) => {
					this.wordCount.words = data.slice(0, 40);
				})
			)
			.subscribe();
	}

	@HostListener('window:scroll', [])
	onWindowScroll() {
		const scrollToTopBtn = document.getElementById('jottoWelcome');

		if (scrollToTopBtn) {
			if (document.body.scrollTop > 20 || document.documentElement.scrollTop > 20) {
				scrollToTopBtn.style.display = 'block';
			} else {
				// scrollToTopBtn.style.display = 'none';
			}
		}
	}

	ngOnInit(): void {
		this.fetchData();

		this.awsUpload.uploadComplete$.subscribe((completed) => {
			if (completed) {
				if (this.notification.external_url && this.notification.shouldRedirect) {
					if (this.deviceService.isDesktop()) {
						const newTab = window.open(this.notification.external_url, '_blank');
						newTab?.focus();
					} else {
						window.open(this.notification.external_url, '_self');
					}
				}
				this.responseService.navigateTo(SCREENS.CHANNEL_FEED_SCREEN, {
					channel: this.channel,
					notification: this.notification,
				});
			}
			this.cdr.detectChanges();
		});

		// find scroll offsetHeight of element with id grayPortion
		const grayPortion = document.getElementById('grayPortion');
		this.grayPortionHeight = grayPortion?.offsetHeight || 0;
		document.addEventListener('scroll', () => {
			const firstPageHeight = 500;
			const scrollPosition = window.scrollY || document.documentElement.scrollTop;
			this.showScrollToNextPageBtn = scrollPosition > firstPageHeight;
		});

		try {
			// Establish MQTT connection
			this.mqttClient = mqtt.connect('wss://' + 'message.jotto.me');

			// Handle connection success
			this.mqttClient.on('connect', () => {
				// Subscribe to a topic after successful connection
				this.observeTopic(`/clouds/${this.notificationID}`);
			});

			// Handle incoming messages
			this.mqttClient.on('message', (topic, message) => {
				const data = JSON.parse(message.toString());
				console.log('data', data);
				const words = data.words.slice(0, 40);
				// Update the word cloud
				// if the word is already in the word cloud, increment the count else add the word to the word cloud
				const updatedWordCount = [...this.wordCount.words];
				// Update the word cloud
				words.forEach((word: string) => {
					const index = updatedWordCount.findIndex((item) => item.name === word);
					if (index >= 0) {
						// Increment count if word exists
						updatedWordCount[index].count++;
					} else {
						// Add new word to the word cloud
						updatedWordCount.push({ id: updatedWordCount.length + 1, name: word, count: 1 });
					}
				});

				// Assign the updated array back to wordCount to trigger change detection
				this.wordCount = { counter: this.wordCount.counter++, words: updatedWordCount, autorefresh: true };
			});
		} catch (err) {
			console.error('Error connecting to MQTT:', err);
		}
	}

	scrollToTop() {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	scrollDiv() {
		this.scrollToTop();
	}

	navigateToJottoSite() {
		window.open(environment.whiteLabelDomain || 'https://www.jotto.video', '_blank');
	}

	initializePage() {
		return this.channelState.findOnebyUUID(this.channelUUID).pipe(
			tap((data: Channel) => {
				this.channel = data;

				if (data?.localization && !this.customLocalizationSet) {
					this.currentLanguage = data.localization;
					this.customLocalizationSet = true;
					this.translate.setDefaultLang(data.localization);
				}
				if (!this.isSTV) this.url = this.channel.share_url;
				else this.url = this.notification.alt_stv_url as string;
				if (this.cookies.get('username') && <[]>JSON.parse(this.cookies.get('follows') || '[]').includes(String(data.id))) {
					this.responseService.currentPage = {
						screen: SCREENS.WELCOME_SCREEN,
						parameters: {
							channel: data,
							notification: this.notification,
						},
					};
					if (window.location.href.includes('subscribe') || !this.notification) {
						this.responseService.navigateTo(SCREENS.CHANNEL_FEED_SCREEN, {
							channel: this.channel,
						});
					} else {
						this.responseService.navigateTo(SCREENS.RESPONSE_SCREEN, {
							channel: this.channel,
							notification: this.notification,
							notificationType: this.notification.response_type ? this.notification.response_type : RESPONSE_TYPES.VIDEO,
						});
					}
					this.user.updateUserTimestamp(this.auth.data.id, { last_accessed: Date.now() }).subscribe();
					this.cdr.detectChanges();
				} else
					this.responseService.currentPage = {
						screen: SCREENS.WELCOME_SCREEN,
						parameters: {
							channel: data,
							notification: this.notification,
						},
					};
				this.cdr.detectChanges();
			}),
			finalize(() => {
				this.ready = true;
			})
		);
	}

    protected readonly environment = environment;
    protected readonly window = window;
}
